import { useState } from 'react'
import { Button, TextArea, InlineAlert } from '../components/BaseComponents'
import { getApiErrorMessage, getCSRFToken } from '../lib/utils'
import usePublicApi from '../hooks/usePublicApi'
import { TEXT_LINK_FOCUS_RING } from '../lib/a11yFocus'

type AccountOnHoldProps = {
	email: string
	password: string
	onBack: () => void
}

// Shown by Login after a correct password for a locked account; the credentials are re-verified server-side by the request.
function AccountOnHold({ email, password, onBack }: AccountOnHoldProps) {
	const publicApi = usePublicApi()
	const [busy, setBusy] = useState(false)
	const [sent, setSent] = useState(false)
	const [error, setError] = useState('')
	const [reason, setReason] = useState('')

	const onRequest = async () => {
		setBusy(true)
		setError('')
		try {
			await publicApi.post('/user/requestReactivation', { email, password, reason, _csrf: await getCSRFToken() })
			setSent(true)
		} catch (err) {
			setBusy(false)
			setError(getApiErrorMessage(err, 'Could not send your request. Please try again later.'))
		}
	}

	return (
		<div className='content w-full max-w-[32rem] px-4 sm:px-0 mx-auto mt-16 mb-8 text-center'>
			<div className='text-charcoal-100 text-2xl'>Your account is currently on hold</div>
			<div className='mt-8 text-charcoal-100'>
				Thank you for being part of the Neoni beta. We're building a major set of new features, so we've paused existing beta accounts while we roll out the updates safely.
			</div>
			<div className='mt-4 text-charcoal-100'>
				Your account and data are safe and unchanged. We'd love you to keep using Neoni. Please send us a short message to reactivate your account.
			</div>
			{sent ? (
				<InlineAlert type='success' className='mt-6'>Request sent — thank you! We'll be in touch by email shortly.</InlineAlert>
			) : (
				<>
					<div className='mt-8 text-left text-sm'>
						<TextArea
							label='Message'
							name='reason'
							rows={4}
							maxLength={1000}
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							disabled={busy}
						/>
					</div>
					<div className='mt-4 flex justify-center'>
						<Button type='Primary' text='Request reactivation' disabled={busy || !reason.trim()} onClick={onRequest} />
					</div>
					<div className='mt-4 text-charcoal-100'>
						Our team will review your request and email you once you're back in. You can also contact our Support Team at <a className={`text-link text-charcoal-100 ${TEXT_LINK_FOCUS_RING}`} href='mailto:support@neoni-app.com'>support@neoni-app.com</a>.
					</div>
				</>
			)}
			{error && <InlineAlert type='error' className='mt-6'>{error}</InlineAlert>}
			<div className='mt-8 text-charcoal-100'>
				<button type='button' className={`text-link text-charcoal-100 ${TEXT_LINK_FOCUS_RING}`} onClick={onBack}>
					Back to login
				</button>
			</div>
		</div>
	)
}

export default AccountOnHold
